import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getArticle } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import { Breadcrumbs } from "../components/layout/PageHero";
import { Reveal } from "../components/motion/Reveal";

export default function NewsDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO(
    article ? `${article.title} — Suvera Academy` : "News — Suvera Academy",
    article ? article.excerpt : "News and insights from Suvera Academy."
  );

  useEffect(() => {
    setLoading(true);
    getArticle(slug)
      .then(setArticle)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen pt-32" data-testid="article-loading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="h-[30vh] animate-pulse rounded-2xl bg-slate-200/70" />
        </div>
      </main>
    );
  }

  if (notFound || !article) {
    return (
      <main className="hero-spotlight flex min-h-screen items-center" data-testid="article-not-found">
        <div className="mx-auto max-w-xl px-6 text-center">
          <h1 className="font-display text-4xl font-semibold text-white">Article Not Found</h1>
          <p className="mt-4 text-white/60">This article does not exist. Browse all news and insights instead.</p>
          <Link to="/news" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy" data-testid="article-not-found-back">
            View All News
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="article-page">
      <section className="hero-spotlight pb-14 pt-32 sm:pt-40">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "News", to: "/news" }, { label: article.title }]} dark />
          <Reveal>
            <span className="inline-flex rounded-full bg-white/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-dot-amber">
              {article.category}
            </span>
            <h1 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="article-title">
              {article.title}
            </h1>
            <p className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/60">
              <span className="inline-flex items-center gap-1.5"><Icon name="Calendar" className="h-4 w-4" />{article.publishedDate}</span>
              <span className="inline-flex items-center gap-1.5"><Icon name="Users" className="h-4 w-4" />{article.author}</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:py-20">
        <Reveal>
          <div className="img-frame">
            <img src={article.image} alt={article.title} className="max-h-[60vh] w-full object-cover lg:max-h-[420px]" loading="lazy" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 space-y-6 text-base leading-relaxed text-slate-700 sm:text-lg" data-testid="article-content">
            {article.content.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/news" className="mt-12 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors duration-200 hover:text-dot-blue" data-testid="article-back-link">
            <Icon name="ArrowRight" className="h-4 w-4 rotate-180" /> Back to News
          </Link>
        </Reveal>
      </section>
      <CTASection />
    </main>
  );
}
